import React, { useState } from 'react';
import { Button, Image, Input, Select, Space, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../providers/dataProvider';
import { ensureFreshAccessToken } from '../../providers/tokenManager';

const ACCEPT = 'image/png,image/webp,image/jpeg';

/** A checkerboard, so transparent artwork reads as transparent. */
const CHECKERBOARD: React.CSSProperties = {
  background:
    'repeating-conic-gradient(#e5e5e5 0% 25%, #ffffff 0% 50%) 50% / 16px 16px',
  border: '1px solid #e8e8e8',
  borderRadius: 8,
  objectFit: 'contain',
};

/** Stored paths are relative to the media root; full URLs pass through. */
export const artworkPreviewUrl = (path?: string): string | undefined => {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE_URL}/media/files/${path.replace(/^\/+/, '')}`;
};

/**
 * Sends one file to `POST /admin/gamification/pet-skins/artwork`, which keeps
 * it byte for byte (the generic media upload re-encodes to JPEG and would
 * drop transparency and animation). Resolves to the `pet-skins/…` path.
 */
async function uploadArtwork(file: File): Promise<string> {
  const token = await ensureFreshAccessToken(API_BASE_URL);
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${API_BASE_URL}/admin/gamification/pet-skins/artwork`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Upload failed (${res.status})`);
  return data.path as string;
}

const Thumb: React.FC<{ path: string; size?: number }> = ({ path, size = 96 }) => (
  <Image src={artworkPreviewUrl(path)} width={size} height={size} style={CHECKERBOARD} />
);

/** One artwork path: type it, or upload a file and get its path filled in. */
export const ArtworkInput: React.FC<{
  /** Passed down by Form.Item, so the label focuses the path input. */
  id?: string;
  value?: string;
  onChange?: (path: string) => void;
  placeholder?: string;
}> = ({ id, value, onChange, placeholder }) => {
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      onChange?.(await uploadArtwork(file));
      message.success('Artwork uploaded');
    } catch (e: any) {
      message.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <Upload
          accept={ACCEPT}
          showUploadList={false}
          beforeUpload={(file) => {
            void upload(file as unknown as File);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={busy}>
            Upload
          </Button>
        </Upload>
      </Space.Compact>
      {value ? <Thumb path={value} /> : null}
    </Space>
  );
};

/** An ordered list of artwork paths — uploads are appended in file order. */
export const ArtworkListInput: React.FC<{
  id?: string;
  value?: string[];
  onChange?: (paths: string[]) => void;
  placeholder?: string;
}> = ({ id, value, onChange, placeholder }) => {
  const [busy, setBusy] = useState(false);
  const paths = value ?? [];

  const uploadAll = async (files: File[]) => {
    setBusy(true);
    try {
      const uploaded: string[] = [];
      for (const file of files) uploaded.push(await uploadArtwork(file));
      onChange?.([...paths, ...uploaded]);
      message.success(`${uploaded.length} artwork file(s) uploaded`);
    } catch (e: any) {
      message.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Select
        id={id}
        mode="tags"
        tokenSeparators={[',']}
        value={paths}
        placeholder={placeholder}
        onChange={(next: string[]) => onChange?.(next)}
      />
      <Upload
        accept={ACCEPT}
        multiple
        showUploadList={false}
        // Collect the whole selection once, keeping the picker's order.
        beforeUpload={(file, fileList) => {
          if (file === fileList[fileList.length - 1]) {
            void uploadAll(fileList as unknown as File[]);
          }
          return false;
        }}
      >
        <Button icon={<UploadOutlined />} loading={busy}>
          Upload files
        </Button>
      </Upload>
      {paths.length ? (
        <Space wrap>
          {paths.map((p) => (
            <Thumb key={p} path={p} size={72} />
          ))}
        </Space>
      ) : null}
    </Space>
  );
};
